import { getOrCreateDeviceId } from './device-id.composable.js';

const FCM_TOKEN_STORAGE_KEY = 'hh_fcm_token';
const FIREBASE_SDK_VERSION = '10.13.2';
const FIREBASE_FALLBACK_CONFIG = {
  apiKey: 'AIzaSyB2eyEPjn9Rk4cWegMqLFfDHorZQTPkdE4',
  authDomain: 'hireheroes-264ac.firebaseapp.com',
  projectId: 'hireheroes-264ac',
  storageBucket: 'hireheroes-264ac.firebasestorage.app',
  messagingSenderId: '163123941714',
  appId: '1:163123941714:web:5d345f0f651a7a37a8ea8b',
};

let firebaseReadyPromise = null;
let foregroundUnsubscribe = null;
let lastForegroundNotification = { key: null, at: 0 };

const isClient = () => typeof window !== 'undefined';
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const normalizeVapidKey = (rawKey) => {
  if (!rawKey) return '';
  return String(rawKey).trim().replace(/^['"]|['"]$/g, '');
};

const getAuthToken = () => {
  if (!isClient()) return null;
  return sessionStorage.getItem('token') || localStorage.getItem('token');
};

const getFirebaseConfig = () => {
  const config = useRuntimeConfig();
  return {
    apiKey: config.public.firebase_api_key || FIREBASE_FALLBACK_CONFIG.apiKey,
    authDomain: config.public.firebase_auth_domain || FIREBASE_FALLBACK_CONFIG.authDomain,
    projectId: config.public.firebase_project_id || FIREBASE_FALLBACK_CONFIG.projectId,
    storageBucket: config.public.firebase_storage_bucket || FIREBASE_FALLBACK_CONFIG.storageBucket,
    messagingSenderId: config.public.firebase_messaging_sender_id || FIREBASE_FALLBACK_CONFIG.messagingSenderId,
    appId: config.public.firebase_app_id || FIREBASE_FALLBACK_CONFIG.appId,
    measurementId: config.public.firebase_measurement_id,
  };
};

const hasFirebaseConfig = (firebaseConfig) => {
  return Boolean(
    firebaseConfig.apiKey &&
      firebaseConfig.projectId &&
      firebaseConfig.messagingSenderId &&
      firebaseConfig.appId
  );
};

const ensureScript = (src) => {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      if (existing.dataset.loaded === 'true') {
        resolve();
        return;
      }

      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.defer = true;
    script.addEventListener(
      'load',
      () => {
        script.dataset.loaded = 'true';
        resolve();
      },
      { once: true }
    );
    script.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)), { once: true });
    document.head.appendChild(script);
  });
};

const registerMessagingServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    await navigator.serviceWorker.ready;
    return registration;
  } catch (error) {
    console.warn('[fcm] Service worker registration failed', error);
    return null;
  }
};

const clearBrowserPushSubscriptions = async () => {
  if (!isClient() || !('serviceWorker' in navigator)) return;

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(
      registrations.map(async (registration) => {
        try {
          const subscription = await registration.pushManager.getSubscription();
          if (subscription) {
            await subscription.unsubscribe();
            console.info('[fcm] Cleared existing browser push subscription');
          }
        } catch (error) {
          console.warn('[fcm] Failed clearing push subscription for a registration', error);
        }
      })
    );
  } catch (error) {
    console.warn('[fcm] Failed to list service worker registrations', error);
  }
};

const initializeFirebaseMessaging = async () => {
  if (!isClient()) return null;

  if (firebaseReadyPromise) return firebaseReadyPromise;

  firebaseReadyPromise = (async () => {
    const firebaseConfig = getFirebaseConfig();
    if (!hasFirebaseConfig(firebaseConfig)) {
      console.info('[fcm] Firebase config missing. Push setup skipped.');
      return null;
    }

    await ensureScript(`https://www.gstatic.com/firebasejs/${FIREBASE_SDK_VERSION}/firebase-app-compat.js`);
    await ensureScript(`https://www.gstatic.com/firebasejs/${FIREBASE_SDK_VERSION}/firebase-messaging-compat.js`);

    const firebase = window.firebase;
    if (!firebase) {
      console.warn('[fcm] Firebase SDK not available on window');
      return null;
    }

    if (!firebase.apps.length) {
      firebase.initializeApp(firebaseConfig);
    }

    let messaging = null;
    try {
      messaging = firebase.messaging();
    } catch (error) {
      console.warn('[fcm] Messaging not supported in this browser', error);
      return null;
    }

    const serviceWorkerRegistration = await registerMessagingServiceWorker();
    return { messaging, serviceWorkerRegistration };
  })();

  return firebaseReadyPromise;
};

const getConversationIdFromPayload = (payload = {}) => {
  const data = payload?.data || {};
  return data.conversation_id || payload?.conversation_id || null;
};

const getChatMessageTypeFromPayload = (payload = {}) => {
  const data = payload?.data || {};
  return data.chat_message_type || data.message_type || null;
};

const navigateToConversation = (conversationId) => {
  if (!conversationId || !isClient()) return;
  const target = `/chat?conversation_id=${encodeURIComponent(conversationId)}`;
  if (window.location.pathname + window.location.search === target) return;
  window.location.href = target;
};

const isViewingSameConversation = (conversationId) => {
  if (!isClient() || !conversationId) return false;
  if (document.visibilityState !== 'visible') return false;
  if (window.location.pathname !== '/chat') return false;

  const params = new URLSearchParams(window.location.search);
  const currentConversationId = params.get('conversation_id');
  return currentConversationId === String(conversationId);
};

const showForegroundNotification = (payload) => {
  if (!isClient() || Notification.permission !== 'granted') return;

  const data = payload?.data || {};
  const title = payload?.notification?.title || payload?.data?.title || 'New message';
  const body = payload?.notification?.body || payload?.data?.body || '';
  const conversationId = getConversationIdFromPayload(payload);
  const chatMessageType = getChatMessageTypeFromPayload(payload);
  const dedupeKey = payload?.messageId || data?.message_id || `${conversationId || 'na'}:${chatMessageType || 'na'}:${title}:${body}`;
  const now = Date.now();

  if (isViewingSameConversation(conversationId)) {
    console.info('[fcm] Suppressing notification: conversation already open');
    return;
  }

  if (lastForegroundNotification.key === dedupeKey && now - lastForegroundNotification.at < 4000) {
    console.info('[fcm] Skipping duplicate foreground notification');
    return;
  }
  lastForegroundNotification = { key: dedupeKey, at: now };

  const notification = new Notification(title, {
    body,
    data: { conversation_id: conversationId, chat_message_type: chatMessageType },
    tag: dedupeKey,
  });

  notification.onclick = () => {
    window.focus();
    navigateToConversation(conversationId);
    notification.close();
  };
};

export const useFcm = async () => {
  const config = useRuntimeConfig();

  const setupForegroundPushListener = async () => {
    const initialized = await initializeFirebaseMessaging();
    if (!initialized || foregroundUnsubscribe) return;

    foregroundUnsubscribe = initialized.messaging.onMessage((payload) => {
      console.info('[fcm] Foreground message received', payload);
      showForegroundNotification(payload);
      const conversationId = getConversationIdFromPayload(payload);
      const chatMessageType = getChatMessageTypeFromPayload(payload);
      if (conversationId && chatMessageType && document.visibilityState !== 'visible') {
        navigateToConversation(conversationId);
      }
    });
  };

  const teardownForegroundPushListener = () => {
    if (typeof foregroundUnsubscribe === 'function') {
      foregroundUnsubscribe();
      foregroundUnsubscribe = null;
    }
  };

  const setFcmTokenOnServer = async (fcmToken) => {
    const authToken = getAuthToken();
    if (!authToken) {
      console.info('[fcm] set-fcm skipped: missing auth token');
      return false;
    }

    try {
      console.info('[fcm] Calling /auth/set-fcm', { hasToken: !!fcmToken });
      await useAxiosPost('auth/set-fcm', { fcmToken });
      console.info('[fcm] /auth/set-fcm success');
      return true;
    } catch (error) {
      console.warn('[fcm] Failed to set token on server', error);
      return false;
    }
  };

  const pushFcmTokenToServer = async (fcmToken) => {
    return await setFcmTokenOnServer(fcmToken);
  };

  const getCurrentFcmToken = async () => {
    if (!isClient()) return null;

    const initialized = await initializeFirebaseMessaging();
    if (!initialized) return null;

    if (!('Notification' in window)) return null;
    if (Notification.permission !== 'granted') return null;

    try {
      const vapidKey = normalizeVapidKey(config.public.firebase_vapid_key);
      const tokenOptions = {
        serviceWorkerRegistration: initialized.serviceWorkerRegistration || undefined,
      };

      if (vapidKey) {
        tokenOptions.vapidKey = vapidKey;
      } else {
        console.warn('[fcm] firebase_vapid_key not set, trying getToken() without VAPID key');
      }

      return await initialized.messaging.getToken(tokenOptions);
    } catch (error) {
      const vapidKey = normalizeVapidKey(config.public.firebase_vapid_key);
      if (error?.name === 'InvalidAccessError' && vapidKey) {
        console.warn('[fcm] Invalid VAPID key, retrying getToken() without vapidKey');
        try {
          return await initialized.messaging.getToken({
            serviceWorkerRegistration: initialized.serviceWorkerRegistration || undefined,
          });
        } catch (retryError) {
          console.warn('[fcm] Retry without VAPID key also failed', retryError);
          return null;
        }
      }
      console.warn('[fcm] Failed to fetch FCM token', error);
      return null;
    }
  };

  const deleteCurrentFcmToken = async () => {
    if (!isClient()) return false;

    const initialized = await initializeFirebaseMessaging();
    if (!initialized) return false;

    try {
      const currentToken = await getCurrentFcmToken();
      if (!currentToken) {
        localStorage.removeItem(FCM_TOKEN_STORAGE_KEY);
        return true;
      }

      if (typeof initialized.messaging.deleteToken === 'function') {
        try {
          await initialized.messaging.deleteToken(currentToken);
        } catch (error) {
          await initialized.messaging.deleteToken();
        }
      }

      await clearBrowserPushSubscriptions();
      localStorage.removeItem(FCM_TOKEN_STORAGE_KEY);
      console.info('[fcm] Existing FCM token deleted');
      return true;
    } catch (error) {
      console.warn('[fcm] Failed to delete existing FCM token', error);
      return false;
    }
  };

  const requestPushPermission = async () => {
    if (!isClient() || !('Notification' in window)) return 'denied';

    try {
      return await Notification.requestPermission();
    } catch (error) {
      console.warn('[fcm] Notification permission request failed', error);
      return 'denied';
    }
  };

  const syncFcmTokenWithServer = async ({ requestPermission = false, forceSync = false } = {}) => {
    if (!isClient()) return null;

    const authToken = getAuthToken();
    if (!authToken) {
      console.info('[fcm] Sync skipped: no auth token');
      return null;
    }

    if (!('Notification' in window)) {
      console.info('[fcm] Sync skipped: Notification API unavailable');
      return null;
    }

    if (requestPermission && Notification.permission !== 'granted') {
      const permission = await requestPushPermission();
      console.info('[fcm] Permission request result:', permission);
      if (permission !== 'granted') return null;
    }

    if (Notification.permission !== 'granted') {
      console.info('[fcm] Sync skipped: permission is', Notification.permission);
      return null;
    }

    let token = null;
    for (let attempt = 0; attempt < 3; attempt++) {
      token = await getCurrentFcmToken();
      if (token) break;
      await wait(700);
    }
    if (!token) {
      token = localStorage.getItem(FCM_TOKEN_STORAGE_KEY);
      if (token) {
        console.info('[fcm] Using cached FCM token');
      }
    }
    if (!token) return null;

    const previousToken = localStorage.getItem(FCM_TOKEN_STORAGE_KEY);
    if (!forceSync && previousToken === token) {
      console.info('[fcm] Sync skipped: token unchanged');
      return token;
    }

    const synced = await setFcmTokenOnServer(token);
    if (synced) {
      localStorage.setItem(FCM_TOKEN_STORAGE_KEY, token);
    }

    return token;
  };

  const refreshFcmTokenOnLogin = async () => {
    if (!isClient()) return null;
    if (!('Notification' in window)) return null;

    let permission = Notification.permission;
    if (permission !== 'granted') {
      permission = await requestPushPermission();
      console.info('[fcm] Login permission result:', permission);
    }

    if (permission !== 'granted') {
      console.warn('[fcm] Permission not granted, skipping token registration');
      return null;
    }

    await deleteCurrentFcmToken();
    await clearBrowserPushSubscriptions();

    let token = null;
    for (let attempt = 0; attempt < 5; attempt++) {
      token = await getCurrentFcmToken();
      if (token) break;
      await wait(1000);
    }

    if (!token) {
      console.warn('[fcm] Unable to generate FCM token after login');
      return null;
    }

    let synced = false;
    for (let attempt = 0; attempt < 3; attempt++) {
      synced = await setFcmTokenOnServer(token);
      if (synced) break;
      await wait(700);
    }
    if (!synced) {
      console.warn('[fcm] Failed to register FCM token on server');
    }

    localStorage.setItem(FCM_TOKEN_STORAGE_KEY, token);
    return token;
  };

  const clearFcmToken = async () => {
    const cleared = await setFcmTokenOnServer(null);
    if (cleared && isClient()) {
      localStorage.removeItem(FCM_TOKEN_STORAGE_KEY);
    }
    return cleared;
  };

  const ensureDeviceId = () => getOrCreateDeviceId();

  return {
    ensureDeviceId,
    requestPushPermission,
    setupForegroundPushListener,
    teardownForegroundPushListener,
    pushFcmTokenToServer,
    refreshFcmTokenOnLogin,
    syncFcmTokenWithServer,
    clearFcmToken,
    navigateToConversation,
  };
};
